import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queueApi } from "@bw/shared/api";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * Leader removes a member from the party (POST /queue/party/{partyId}/kick).
 *
 * Party-roster-hygiene: the leader-only counterpart to `useLeaveParty`'s
 * self-service exit — used to clear a member whose drop-in session has
 * blocked in `PartyResponse.eligibility` so the leader can place the party.
 * The request carries the eligibility roster's opaque member_id; it never
 * exposes or submits the member's user id. Authorization (leader-only,
 * not-self, not-while-queued, target must be a current member) is entirely
 * server-enforced. On settlement `party.detail(partyId)` is invalidated so the
 * eligibility roster re-reads server truth.
 * Errors are NOT swallowed — a 403 (not the leader / not a member), 404
 * (missing party), or 409 (self-kick / party queued) surfaces on
 * `mutation.error` for the page to map.
 */
export function useKickPartyMember(partyId: string) {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, string>({
    mutationFn: (targetMemberId: string) =>
      queueApi.kickPartyMember(partyId, targetMemberId),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.party.detail(partyId),
        refetchType: "active",
      });
    },
  });
}
